import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { timeAgo } from '@/lib/timeAgo';
import { useToast } from '@/lib/useToast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SkeletonRows } from '@/ui/Skeleton';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorBanner } from '@/ui/ErrorBanner';
import { Field } from '@/ui/Field';

/**
 * Generic notes/records board shared by the five "Growth & Ops" screens
 * (social media, influencer outreach, venue relations, campus growth, dev
 * timeline roadmap). One admin_notes table + 3 RPCs behind a `page` key —
 * see supabase/sql/admin_notes.sql (not yet applied to prod; this component
 * degrades gracefully — see `isNotesRpcMissing` below — when it isn't).
 */

export type NotesBoardMetaField = {
  /** Key inside the note's `metadata` jsonb, e.g. "handle" or "campus". */
  key: string;
  label: string;
  placeholder?: string;
  /** Optional prefill chips shown under the input — freeform, not an enum. */
  suggestions?: string[];
};

export type AdminNote = {
  id: string;
  page: string;
  title: string;
  body: string | null;
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
};

type NoteDraft = {
  title: string;
  body: string;
  status: string;
  metadata: Record<string, string>;
};

type Props = {
  /** Scopes notes to one screen — see suggested values at the bottom of admin_notes.sql. */
  page: string;
  /** If given, status is a <select> of these values; otherwise a free-text input (default "open"). */
  statusOptions?: string[];
  /** Extra structured text fields merged into `metadata` on save (e.g. handle/platform, venue/contact). */
  metadataFields?: NotesBoardMetaField[];
  /** Group the list into sections by this metadata field's value (e.g. "campus"). */
  groupByField?: string;
  newButtonLabel?: string;
  emptyTitle?: string;
  emptyHint?: string;
};

function isNotesRpcMissing(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('admin_list_notes') ||
    m.includes('admin_upsert_note') ||
    m.includes('admin_delete_note') ||
    m.includes('42883') ||
    m.includes('could not find the function')
  );
}

function metaString(v: unknown): string {
  if (v == null) return '';
  return typeof v === 'string' ? v : String(v);
}

function cleanMetadata(meta: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (v && v.trim()) out[k] = v.trim();
  }
  return out;
}

function draftFromNote(note: AdminNote | null, fields: NotesBoardMetaField[] | undefined): NoteDraft {
  const metadata: Record<string, string> = {};
  (fields ?? []).forEach((f) => {
    metadata[f.key] = note ? metaString(note.metadata[f.key]) : '';
  });
  return {
    title: note?.title ?? '',
    body: note?.body ?? '',
    status: note?.status ?? '',
    metadata,
  };
}

/** Loose heuristic tone mapping so free-text statuses still read as one system. */
function statusTone(status: string): 'success' | 'warning' | 'danger' | 'accent' | 'neutral' {
  const s = status.toLowerCase();
  if (/(shipped|posted|partnered|done|responded)/.test(s)) return 'success';
  if (/(blocked|declined|paused)/.test(s)) return 'danger';
  if (/(progress|active|talks|contacted|negotiat|schedul|draft|planning|planned|idea)/.test(s)) return 'accent';
  return 'neutral';
}

function StatusPill({ status }: { status: string }) {
  return <span className={`admin-status admin-status-${statusTone(status)}`}>{status}</span>;
}

function NoteEditor({
  statusOptions,
  metadataFields,
  draft,
  busy,
  submitLabel,
  onChange,
  onCancel,
  onSubmit,
}: {
  statusOptions?: string[];
  metadataFields?: NotesBoardMetaField[];
  draft: NoteDraft;
  busy: boolean;
  submitLabel: string;
  onChange: (next: NoteDraft) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const canSubmit = draft.title.trim().length > 0 && !busy;
  return (
    <div className="space-y-3">
      <Field label="Title">
        <input
          type="text"
          value={draft.title}
          onChange={(e) => onChange({ ...draft, title: e.target.value })}
          placeholder="Title"
          className="admin-input"
          autoFocus
        />
      </Field>
      {(metadataFields ?? []).map((f) => (
        <Field key={f.key} label={f.label}>
          <input
            type="text"
            value={draft.metadata[f.key] ?? ''}
            onChange={(e) => onChange({ ...draft, metadata: { ...draft.metadata, [f.key]: e.target.value } })}
            placeholder={f.placeholder}
            className="admin-input"
          />
          {f.suggestions && f.suggestions.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {f.suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-active={draft.metadata[f.key] === s}
                  onClick={() => onChange({ ...draft, metadata: { ...draft.metadata, [f.key]: s } })}
                  className="admin-pill"
                >
                  {s}
                </button>
              ))}
            </div>
          ) : null}
        </Field>
      ))}
      <Field label="Body">
        <textarea
          rows={4}
          value={draft.body}
          onChange={(e) => onChange({ ...draft, body: e.target.value })}
          placeholder="Notes…"
          className="admin-input !h-auto !py-2 resize-y leading-relaxed"
        />
      </Field>
      <Field label="Status">
        {statusOptions && statusOptions.length > 0 ? (
          <select
            value={draft.status || statusOptions[0]}
            onChange={(e) => onChange({ ...draft, status: e.target.value })}
            className="admin-input"
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={draft.status}
            onChange={(e) => onChange({ ...draft, status: e.target.value })}
            placeholder="open"
            className="admin-input"
          />
        )}
      </Field>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={onSubmit}
          className="admin-btn admin-btn-primary admin-btn-sm"
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="admin-btn admin-btn-ghost admin-btn-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

function NoteCard({
  note,
  metadataFields,
  statusOptions,
  onEdit,
  onDelete,
}: {
  note: AdminNote;
  metadataFields?: NotesBoardMetaField[];
  statusOptions?: string[];
  onEdit: () => void;
  onDelete: () => void;
}) {
  void statusOptions;
  const metaChips = (metadataFields ?? [])
    .map((f) => ({ field: f, value: metaString(note.metadata[f.key]) }))
    .filter((m) => m.value);

  return (
    <div className="admin-card space-y-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="admin-label-primary">{note.title}</h3>
          <StatusPill status={note.status} />
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button type="button" onClick={onEdit} className="admin-btn admin-btn-ghost admin-btn-sm">
            Edit
          </button>
          <button type="button" onClick={onDelete} className="admin-btn admin-btn-ghost admin-btn-sm">
            Delete
          </button>
        </div>
      </div>
      {metaChips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {metaChips.map((m) => (
            <span key={m.field.key} className="admin-chip-id">
              {m.field.label}: {m.value}
            </span>
          ))}
        </div>
      ) : null}
      {note.body ? <p className="text-[13px] leading-relaxed text-muted whitespace-pre-wrap">{note.body}</p> : null}
      <p className="text-[11px] text-mutedDim">
        created {timeAgo(note.created_at)}
        {note.updated_at && note.updated_at !== note.created_at ? ` · updated ${timeAgo(note.updated_at)}` : ''}
      </p>
    </div>
  );
}

export function NotesBoard({
  page,
  statusOptions,
  metadataFields,
  groupByField,
  newButtonLabel = 'Add note',
  emptyTitle = 'No notes yet',
  emptyHint = 'Add the first one above.',
}: Props) {
  const [notes, setNotes] = useState<AdminNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<NoteDraft>(() => draftFromNote(null, metadataFields));
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<NoteDraft | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<AdminNote | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const { showError, showSuccess, toastNode } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_list_notes', { p_page: page });
      if (rpcErr) {
        if (isNotesRpcMissing(rpcErr.message)) {
          setUnavailable(true);
          setNotes([]);
          return;
        }
        throw rpcErr;
      }
      setUnavailable(false);
      setNotes((data ?? []) as AdminNote[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load notes');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const startCreate = useCallback(() => {
    setCreateDraft(draftFromNote(null, metadataFields));
    setCreating(true);
  }, [metadataFields]);

  const submitCreate = useCallback(async () => {
    if (!createDraft.title.trim()) return;
    setSaving(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_upsert_note', {
        p_id: null,
        p_page: page,
        p_title: createDraft.title.trim(),
        p_body: createDraft.body.trim() || null,
        p_status: createDraft.status.trim() || null,
        p_metadata: cleanMetadata(createDraft.metadata),
      });
      if (rpcErr) throw rpcErr;
      showSuccess('Note added');
      setCreating(false);
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to add note');
    } finally {
      setSaving(false);
    }
  }, [createDraft, page, load, showSuccess, showError]);

  const startEdit = useCallback(
    (note: AdminNote) => {
      setEditingId(note.id);
      setEditDraft(draftFromNote(note, metadataFields));
    },
    [metadataFields],
  );

  const submitEdit = useCallback(async () => {
    if (!editingId || !editDraft || !editDraft.title.trim()) return;
    setSaving(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_upsert_note', {
        p_id: editingId,
        p_page: page,
        p_title: editDraft.title.trim(),
        p_body: editDraft.body.trim() || null,
        p_status: editDraft.status.trim() || null,
        p_metadata: cleanMetadata(editDraft.metadata),
      });
      if (rpcErr) throw rpcErr;
      showSuccess('Note updated');
      setEditingId(null);
      setEditDraft(null);
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to update note');
    } finally {
      setSaving(false);
    }
  }, [editingId, editDraft, page, load, showSuccess, showError]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_delete_note', { p_id: deleteTarget.id });
      if (rpcErr) throw rpcErr;
      showSuccess('Note deleted');
      setDeleteTarget(null);
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to delete note');
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTarget, load, showSuccess, showError]);

  const groups = useMemo(() => {
    if (!groupByField) return null;
    const byGroup = new Map<string, AdminNote[]>();
    for (const n of notes) {
      const key = metaString(n.metadata[groupByField]) || 'Ungrouped';
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key)!.push(n);
    }
    return Array.from(byGroup.entries()).sort((a, b) => {
      if (a[0] === 'Ungrouped') return 1;
      if (b[0] === 'Ungrouped') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [notes, groupByField]);

  function renderCard(note: AdminNote) {
    if (editingId === note.id && editDraft) {
      return (
        <div key={note.id} className="admin-card">
          <NoteEditor
            statusOptions={statusOptions}
            metadataFields={metadataFields}
            draft={editDraft}
            busy={saving}
            submitLabel="Save changes"
            onChange={setEditDraft}
            onCancel={() => {
              setEditingId(null);
              setEditDraft(null);
            }}
            onSubmit={() => void submitEdit()}
          />
        </div>
      );
    }
    return (
      <NoteCard
        key={note.id}
        note={note}
        metadataFields={metadataFields}
        statusOptions={statusOptions}
        onEdit={() => startEdit(note)}
        onDelete={() => setDeleteTarget(note)}
      />
    );
  }

  return (
    <div className="space-y-5">
      {unavailable ? (
        <div className="rounded-lg border border-border/60 bg-canvas/40 px-4 py-3 text-xs leading-relaxed text-mutedDim">
          Notes board is not enabled yet — run supabase/sql/admin_notes.sql against production.
        </div>
      ) : (
        <>
          {error ? <ErrorBanner>{error}</ErrorBanner> : null}

          <section className="admin-card">
            <div className="mb-1 flex items-center justify-between">
              <div className="admin-eyebrow">New note</div>
              {!creating ? (
                <button type="button" onClick={startCreate} className="admin-btn admin-btn-secondary admin-btn-sm">
                  + New
                </button>
              ) : null}
            </div>
            {creating ? (
              <div className="pt-3">
                <NoteEditor
                  statusOptions={statusOptions}
                  metadataFields={metadataFields}
                  draft={createDraft}
                  busy={saving}
                  submitLabel={newButtonLabel}
                  onChange={setCreateDraft}
                  onCancel={() => setCreating(false)}
                  onSubmit={() => void submitCreate()}
                />
              </div>
            ) : null}
          </section>

          {loading ? (
            <SkeletonRows rows={4} />
          ) : notes.length === 0 ? (
            <EmptyState title={emptyTitle} hint={emptyHint} />
          ) : groups ? (
            <div className="space-y-6">
              {groups.map(([groupName, groupNotes]) => (
                <div key={groupName} className="space-y-3">
                  <div className="admin-eyebrow">
                    {groupName} <span className="text-mutedDim normal-case">({groupNotes.length})</span>
                  </div>
                  <div className="space-y-3">{groupNotes.map(renderCard)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">{notes.map(renderCard)}</div>
          )}
        </>
      )}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title={`Delete "${deleteTarget?.title ?? ''}"?`}
        message="This permanently removes the note. This cannot be undone."
        confirmLabel="Delete"
        danger
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
      {toastNode}
    </div>
  );
}
